/** Process-time bucket filter values — shared with the recent-failures API. */
export const ProcessTimeBucket = {
  UnderOneMin: '0-1',
  OneToTwoMin: '1-2',
  OverTwoMin: '2+',
} as const

export type ProcessTimeBucket =
  (typeof ProcessTimeBucket)[keyof typeof ProcessTimeBucket]

// `as const` object so call-sites use BotType.WEB_AGENT instead of a magic
// string; the type stays an open union so a new backend value still type-checks
// and falls through to neutral rendering. Same pattern as ProcessTimeBucket.
export const BotType = {
  IVR: 'IVR',
  WEB_AGENT: 'WEB_AGENT',
} as const

export type BotType =
  | (typeof BotType)[keyof typeof BotType]
  | (string & Record<string, never>)

export interface GetPaymentBotDashboardRequestDto {
  /** 0-indexed calendar month (0 = Jan, 11 = Dec) — matches DB pa.month and the
   * payment-assignment API. */
  month: number
  year: number
}

export interface GetCarrierDetailRequestDto
  extends GetPaymentBotDashboardRequestDto {
  /** Carrier name to scope to; absent = operation-wide aggregate view. */
  carrierName?: string
}

export interface GetPaymentBotAttemptExportRequestDto
  extends GetPaymentBotDashboardRequestDto {
  /** Optional carrier scope; absent exports all carriers. */
  carrierName?: string
}

export interface GetRecentFailuresRequestDto
  extends GetPaymentBotDashboardRequestDto {
  /**
   * Error breakdown bucket clicked in the UI. Filters the recent-failures table
   * server-side; 'UNKNOWN' targets null-code rows (EMS-8614).
   */
  errorCode?: string
  /** 1-based page number; backend defaults to 1. */
  page?: number
  /** Rows per page; backend defaults to 50. */
  limit?: number
  /** Multi-select carrier name filter. */
  carrierNames?: string[]
  /** Column key to sort by — forwarded to ORDER BY server-side. */
  sortBy?: string
  /** 'asc' | 'desc' — defaults to 'desc' on the server. */
  sortOrder?: 'asc' | 'desc'
  /** Process-time bucket filter — UnderOneMin (< 1 min), OneToTwoMin (1–2 min), OverTwoMin (> 2 min). */
  processTimeBucket?: ProcessTimeBucket
  /** 2nd Attempt Success filter */
  secondAttemptSuccess?: boolean
  /** Benefit ID search filter */
  benefitId?: string
}

export interface CarrierSuccessRateDto {
  carrierId: number
  carrierName: string
  botType: BotType | null
  /** COUNT(DISTINCT benefit_id) from payment_assignment WHERE payment_type='BOT' — includes PENDING */
  totalBenefits: number
  /** benefits with at least one COMPLETED task (final-status succeeded) */
  completedCount: number
  /** benefits with only FAILED tasks, never COMPLETED */
  failedCount: number
  /** benefits with no terminal task yet (PENDING_PAYMENT/QUEUED/PROCESSING) */
  pendingCount: number
  /** completedCount / totalBenefits — denominator includes PENDING, intentionally < eventualRate */
  successRate: number
  /** first-attempt success: distinct benefits whose earliest task COMPLETED */
  firstAttemptRate: number
  /** eventual success: distinct benefits that ever COMPLETED (denominator = completed+failed only) */
  eventualRate: number
  medianProcessingTimeSec: number
  /** this month's succeeded benefits − previous month's succeeded benefits */
  deltaCompletedVsPrev: number
}

export interface GetCarrierSummaryResponseDto {
  carriers: CarrierSuccessRateDto[]
  /**
   * Operation-wide median processing time (pooled benefit events, server-side
   * PERCENTILE_CONT) — identical to the all-carrier detail KPI. Use this for
   * the summary KPI strip instead of averaging per-carrier medians.
   */
  overallMedianProcessingTimeSec: number
  period: { startDate: string; endDate: string }
}

export interface ErrorBreakdownItemDto {
  errorCode: string
  count: number
  recoveredCount: number
  percentage: number
  cumulativePercentage: number
}

export interface CarrierMonthHistoryItemDto {
  /** YYYY-MM */
  period: string
  benefits: number
  completed: number
  failed: number
  successRate: number
  failRate: number
  /** vs the previous (older) month; null for the oldest row */
  deltaPp: number | null
}

export interface RecentFailureDto {
  /** Latest failed task id for this benefit — used to link to CloudWatch logs */
  taskId: string
  paymentAssignmentId: number
  benefitId: string
  /** owning carrier — disambiguates rows in the 'all'-carrier aggregate view */
  carrierName: string
  /** IVR | WEB_AGENT | API — drives the per-bot-type process-time health color */
  botType: BotType | null
  errorCode: string | null
  /** Insured person full name. null when person record not found. */
  insuredName: string | null
  failedAt: string
  /** Event-based processing time in seconds. null when no paired benefit_event rows. */
  processTimeSec: number | null
  secondAttemptSuccess: boolean
  note: string | null
  /**
   * Carrier upstream message (from the TechnicalError metaData block) shown in
   * the Note column for TECHNICAL_ERROR rows. null for non-technical failures.
   * The rest of the metaData (incl. raw member ASR in extra.speechResult) is no
   * longer sent by the API — only this single operator-facing string. EMS-8614.
   */
  upstreamMessage: string | null
}

export interface GetRecentFailuresResponseDto {
  recentFailures: RecentFailureDto[]
  /** total matching rows across all pages */
  total: number
}

export interface GetCarrierDetailResponseDto {
  /** 0 for the operation-wide aggregate ("All Carriers") view */
  carrierId: number
  carrierName: string
  botType: BotType | null
  /** COUNT(DISTINCT benefit_id) from payment_assignment WHERE payment_type='BOT' — includes PENDING */
  totalBenefits: number
  /** benefits with at least one COMPLETED task */
  completedCount: number
  /** benefits with only FAILED tasks, never COMPLETED */
  failedCount: number
  /** benefits not yet in terminal state */
  pendingCount: number
  /** completedCount / totalBenefits (denominator includes PENDING) */
  successRate: number
  /** first-attempt success rate (denominator = completed+failed only) */
  firstAttemptRate: number
  /** eventual success rate (denominator = completed+failed only) */
  eventualRate: number
  errorBreakdown: ErrorBreakdownItemDto[]
  monthHistory: CarrierMonthHistoryItemDto[]
  recentFailures: RecentFailureDto[]
  medianProcessingTimeSec: number
  period: { startDate: string; endDate: string }
}
